/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ILinkProps, IStackProps, Link, Pivot, PivotItem, PivotLinkSize, Stack, StackItem } from '@fluentui/react';
import { Property } from 'csstype';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { EditableTitle } from 'src/core/components/EditableTitle';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LimitedAccessToFeaturesMessageBar } from 'src/core/components/LimitedAccessToFeaturesMessageBar';
import { LuisText, LuisTextProps } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { entitiesPageMessages } from 'src/luis/appShell/buildPane/entitiesPage/entitiesPageMessages';
import { useEntities } from 'src/luis/projects/entities/hooks/useEntities';
import { CompositeEntity } from 'src/luis/projects/entities/types/CompositeEntity';
import { prettyPrintEntity } from 'src/luis/projects/entities/utils/entityHelpers';
import { ModelType } from 'src/luis/types/ModelType';
import { CompositeEntityStore } from 'src/luis/types/stores/entities/CompositeEntityStore';
import { EntityStore } from 'src/luis/types/stores/entities/EntityStore';
import { HierarchicalEntityStore } from 'src/luis/types/stores/entities/HierarchicalEntityStore';
import { ListEntityStore } from 'src/luis/types/stores/entities/ListEntityStore';
import { RegexEntityStore } from 'src/luis/types/stores/entities/RegexEntityStore';
import { SimpleEntityStore } from 'src/luis/types/stores/entities/SimpleEntityStore';
import { CompositeEntityDetails } from 'src/luis/verticals/entityDetails/components/compositeEntityDetails/CompositeEntityDetails';
import { EntityExamples } from 'src/luis/verticals/entityDetails/components/entityExamples/EntityExamples';
import { EntityRoles } from 'src/luis/verticals/entityDetails/components/EntityRoles';
import { HierarchicalEntityDetails } from 'src/luis/verticals/entityDetails/components/hierarchicalEntityDetails/HierarchicalEntityDetails';
import { ListEntityDetails } from 'src/luis/verticals/entityDetails/components/listEntityDetails/ListEntityDetails';
import { MLEntityDetails } from 'src/luis/verticals/entityDetails/components/MLEntityDetails';
import { RegexEntityDetails } from 'src/luis/verticals/entityDetails/components/RegexEntityDetails';
import { PatternTableContainer } from 'src/luis/verticals/patternsTable/PatternTableContainer';
import { important } from 'stylist-react';

export type EntityDetailsProps = {
    entityStore: EntityStore;
};

const messages = defineMessages({
    schema: {
        id: 'entityDetails.entityDetails.schema',
        defaultMessage: 'Schema and features'
    },
    listItems: {
        id: 'entityDetails.entityDetails.listItems',
        defaultMessage: 'List items'
    },
    regularExpression: {
        id: 'entityDetails.entityDetails.regularExpression',
        defaultMessage: 'Regular expression'
    },
    childEntities: {
        id: 'entityDetails.entityDetails.childEntities',
        defaultMessage: 'Child entities'
    },
    examples: {
        id: 'entityDetails.entityDetails.examples',
        defaultMessage: 'Examples'
    },
    roles: {
        id: 'entityDetails.entityDetails.roles',
        defaultMessage: 'Roles'
    },
    simpleEntityDescription: {
        id: 'entityDetails.entityDetails.simpleEntityDescription',
        defaultMessage: 'An ML entity can be composed of smaller subentities, each of which can have its own ML features.'
    },
    simpleEntityLink: {
        id: 'entityDetails.entityDetails.simpleEntityLink',
        defaultMessage: 'Learn more about ML features.'
    },
    regexEntityDescription: {
        id: 'entityDetails.entityDetails.regexEntityDescription',
        defaultMessage:
            'Regular expression entities extract an entity based on a regular expression pattern you provide. Regex entities are a good fit for data that are consistently formatted with consistent variations.'
    },
    regexEntityLink: {
        id: 'entityDetails.entityDetails.regexEntityLink',
        defaultMessage: 'Learn more about Regex entities.'
    },
    listEntityDescription: {
        id: 'entityDetails.entityDetails.listEntityDescription',
        defaultMessage:
            'List entities represent a fixed, closed set of related words along with their synonyms. List entities are extracted by an exact text match and can be resolved to a normalized value.'
    },
    listEntityLink: {
        id: 'entityDetails.entityDetails.listEntityLink',
        defaultMessage: 'Learn more about List entities.'
    },
    hierarchicalEntityDescription: {
        id: 'entityDetails.entityDetails.hierarchicalEntityDescription',
        defaultMessage: 'Hiearchical entities are no longer supported.'
    },
    compositeEntityDescription: {
        id: 'entityDetails.entityDetails.compositeEntityDescription',
        defaultMessage: 'A composite entity is a group of related entities, also known as child entities.'
    },
    compositeEntityLink: {
        id: 'entityDetails.entityDetails.compositeEntityLink',
        defaultMessage: 'Learn more about upgrading composite entities.'
    },
    entityExamplesDescription: {
        id: 'entityDetails.entityDetails.entityExamplesDescription',
        defaultMessage: 'Below are the examples you have labeled with this entity and the intents to which they are assigned.'
    },
    nonMLEntityExamplesDescription: {
        id: 'entityDetails.entityDetails.nonMLEntityExamplesDescription',
        defaultMessage:
            'Below are the examples you have labeled with this entity with roles attached and the intents to which they are assigned.'
    },
    upgradeText: {
        id: 'entityDetails.entityDetails.upgradeText',
        defaultMessage: 'Upgrade now'
    }
});

const detailsTheme = getScopedTheme('EntityDetails');

const { styleComponent } = getStylistV2('EntityDetails');

const EntityTypeSubtitle = styleComponent<LuisTextProps>(LuisText)('EntityTypeSubtitle', theme =>
    important({
        fontWeight: theme.typeSubtitleFontWeight as Property.FontWeight,
        color: theme.typeSubtitleColor
    })
);

const PivotItemContainer = styleComponent<IStackProps>(Stack)('PivotItemContainer', { paddingTop: '24px', paddingLeft: '13px' });
const UpgradeLink = styleComponent<ILinkProps>(Link)('UpgradeLink', { lineHeight: '12px' });

/**
 * The component rendering entity details.
 */
export const EntityDetails = observer((props: EntityDetailsProps) => {
    const { entityStore } = props;
    const { dispatcher, rootStore } = useGlobalState();
    const { localize } = useLocalization();
    /**
     * @todo
     * to be removed when stores types are the main types used.
     */
    const { entities } = useEntities();

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite } = mapUserRoleToUserActions(userRole);

    const isComposite = entityStore.typeId === ModelType.Composite;
    const isHierarchical = entityStore.typeId === ModelType.Hierarchical;
    const isPatternAny = entityStore.typeId === ModelType.PatternAny;
    const isPrebuilt = entityStore.typeId === ModelType.Prebuilt;
    const isML =
        entityStore.typeId === ModelType.Simple ||
        entityStore.typeId === ModelType.Composite ||
        entityStore.typeId === ModelType.Hierarchical;

    const activitySource = 'EntityDetailsPage';

    /**
     * A function to generate the text for the pivot responsible
     * for displaying entity type related details.
     */
    const getPivotHeaderText = () => {
        switch (entityStore.typeId) {
            case ModelType.Simple:
                return localize(messages.schema);
            case ModelType.List:
                return localize(messages.listItems);
            case ModelType.Regex:
                return localize(messages.regularExpression);
            case ModelType.Composite:
            case ModelType.Hierarchical:
                return localize(messages.childEntities);
            default:
                return '';
        }
    };

    /**
     * Returns the entity description based on the
     * entity type.
     */
    const renderTypeSpecificDescription = () => {
        let description: string;
        let linkText: string;
        let linkHref: string;

        switch (entityStore.typeId) {
            case ModelType.Composite:
                description = localize(messages.compositeEntityDescription);
                linkText = localize(messages.compositeEntityLink);
                linkHref = 'https://go.microsoft.com/fwlink/?linkid=2128287';
                break;
            case ModelType.List:
                description = localize(messages.listEntityDescription);
                linkText = localize(messages.listEntityLink);
                linkHref = 'https://docs.microsoft.com/azure/cognitive-services/luis/reference-entity-list?tabs=V2';
                break;
            case ModelType.Regex:
                description = localize(messages.regexEntityDescription);
                linkText = localize(messages.regexEntityLink);
                linkHref = 'https://docs.microsoft.com/azure/cognitive-services/luis/reference-entity-regular-expression?tabs=V2';
                break;
            case ModelType.Simple:
                description = localize(messages.simpleEntityDescription);
                linkText = localize(messages.simpleEntityLink);
                linkHref = 'https://docs.microsoft.com/azure/cognitive-services/luis/luis-concept-feature';
                break;
            case ModelType.Hierarchical:
                description = localize(messages.hierarchicalEntityDescription);
                linkText = '';
                linkHref = '';
                break;
            default:
                return null;
        }

        return (
            <LuisText variant="medium" color={detailsTheme.textColor}>
                {description}
                &nbsp;
                <ExternalLink href={linkHref}>{linkText}</ExternalLink>
            </LuisText>
        );
    };

    /**
     * Renders the correct details resolver for the given
     * entity using its type to pick the correct component.
     */
    const renderTypeSpecificDetails = (): JSX.Element => {
        switch (entityStore.typeId) {
            case ModelType.Composite:
                return <CompositeEntityDetails entity={entities.find(e => e.id === entityStore.id) as CompositeEntity} />;
            case ModelType.List:
                return <ListEntityDetails entityStore={entityStore as ListEntityStore} />;
            case ModelType.Regex:
                return <RegexEntityDetails entityStore={entityStore as RegexEntityStore} editingAllowed={canWrite} />;
            case ModelType.Simple:
                return (
                    <MLEntityDetails
                        entityStore={entityStore as SimpleEntityStore}
                        userRole={userRole}
                        displayedUserRole={displayedUserRole}
                    />
                );
            case ModelType.Hierarchical:
                return <HierarchicalEntityDetails entityStore={entityStore as HierarchicalEntityStore} />;
            default:
                return null;
        }
    };

    /**
     * Renames the entity with the given new name.
     *
     * @param newName The new name to name the entity with.
     */
    const onRenameEntity = (newName: string): void => {
        const payload = {
            id: entityStore.id,
            name: newName,
            toastData: {
                running: { title: localize(entitiesPageMessages.updateEntityToastStart) },
                completed: { title: localize(entitiesPageMessages.updateEntityToastSuccess) },
                failed: { title: localize(entitiesPageMessages.updateEntityToastFailed) }
            }
        };

        switch (entityStore.typeId) {
            case ModelType.Simple:
                dispatcher.dispatch('renameSimpleEntity', payload);

                return;
            case ModelType.Composite:
                const children = entities.filter(e => (entityStore as CompositeEntityStore).children.find(c => c === e.id));
                dispatcher.dispatch('updateCompositeEntity', { ...payload, children });

                return;
            case ModelType.List:
                dispatcher.dispatch('updateListEntity', { ...payload, sublists: (entityStore as ListEntityStore).sublists });

                return;
            case ModelType.PatternAny:
                dispatcher.dispatch('renamePatternAnyEntity', payload);

                return;
            case ModelType.Regex:
                dispatcher.dispatch('updateRegexEntity', { ...payload, pattern: (entityStore as RegexEntityStore).pattern });

                return;
            case ModelType.Hierarchical:
                dispatcher.dispatch('renameHierarchicalEntity', {
                    ...payload,
                    children: (entityStore as HierarchicalEntityStore).children
                });

                return;
            default:
                throw Error(`Type ${entityStore.typeId} isn't a valid entity type`);
        }
    };

    return (
        <Stack tokens={{ childrenGap: 50 }}>
            <StackItem>
                <EditableTitle
                    isEditable={entityStore.typeId !== ModelType.Prebuilt && canWrite}
                    title={entityStore.name}
                    onUpdateTitle={onRenameEntity}
                />

                <Stack horizontal tokens={{ childrenGap: 14 }}>
                    <EntityTypeSubtitle as="h2" block variant="small">
                        {prettyPrintEntity(entityStore.typeId)}
                    </EntityTypeSubtitle>

                    {isComposite && (
                        <UpgradeLink
                            data-automation-id="upgradeButton"
                            onClick={() => dispatcher.dispatch('updateEntitiesMigrationModalVisibility', { isVisible: true })}
                            disabled={!canWrite}>
                            <LuisText variant="smallPlus" lineHeight="12px">
                                {localize(messages.upgradeText)}
                            </LuisText>
                        </UpgradeLink>
                    )}
                </Stack>
            </StackItem>

            {!canWrite && <LimitedAccessToFeaturesMessageBar displayedUserRole={displayedUserRole} />}

            <Pivot linkSize={PivotLinkSize.large}>
                {!isPatternAny && !isPrebuilt && (
                    <PivotItem headerText={getPivotHeaderText()} data-automation-id="detailsPivot">
                        <PivotItemContainer tokens={{ childrenGap: 24 }}>
                            {renderTypeSpecificDescription()}

                            {renderTypeSpecificDetails()}
                        </PivotItemContainer>
                    </PivotItem>
                )}

                <PivotItem headerText={localize(messages.examples)} data-automation-id="examplesPivot">
                    <PivotItemContainer tokens={{ childrenGap: 32 }}>
                        <LuisText variant="medium" color={detailsTheme.textColor}>
                            {localize(isML || isPatternAny ? messages.entityExamplesDescription : messages.nonMLEntityExamplesDescription)}
                        </LuisText>

                        {isPatternAny ? (
                            <PatternTableContainer
                                appliedFilters={[
                                    {
                                        id: entityStore.id,
                                        name: entityStore.name,
                                        isChecked: true,
                                        filterType: 'entities'
                                    }
                                ]}
                                loggerActivitySource={activitySource}
                                areAdvancedOptionsEnabled={false}
                            />
                        ) : (
                            <EntityExamples entityStore={entityStore} />
                        )}
                    </PivotItemContainer>
                </PivotItem>

                {!isHierarchical && (
                    <PivotItem headerText={localize(messages.roles)} data-automation-id="rolesPivot">
                        <EntityRoles entityStore={entityStore} />
                    </PivotItem>
                )}
            </Pivot>
        </Stack>
    );
});
